import {
  applyMiddleware,
  combineReducers,
  createStore,
  compose,
  Store,
} from 'redux';
import thunkMiddleware from 'redux-thunk';

/* Reducers */

import { analyticsMiddleware } from 'services';

import { accountsReducer, Accounts } from 'reducers/Accounts';

import { attachmentsReducer, Attachments } from 'reducers/Attachments';

import { attachmentsAWSReducer, AttachmentsAWS } from './AttachmentsAWS';

import { bulkCloseReducer, BulkClose } from 'reducers/BulkClose';

import { categoriesReducer, Categories } from 'reducers/Category';

import {
  collectionsManagerReducer,
  CollectionsManager,
} from 'reducers/CollectionsManager';

import { exportTicketsReducer, ExportTickets } from 'reducers/ExportTickets';

import { interceptReducer, InterceptModal } from 'reducers/InterceptModal';

import { resumeTicketModalReducer, ResumeTicket } from 'reducers/ResumeTicket';

import { newCommentReducer, NewComment } from 'reducers/NewComment';

import { newTicketReducer, NewTicket } from 'reducers/NewTicket';

import { npsReducer, Rating } from 'reducers/Nps';

import {
  customerSatisfactionSurveyReducer,
  RatingCSAT,
} from 'reducers/CustomerSatisfactionSurvey';

import { paginationReducer, Pagination } from 'reducers/Pagination';

import { portalPreferencesReducer } from 'reducers/PortalPreferences';

import { recipientsReducer, Recipients } from 'reducers/Recipients';

import { optInReducer, OptInModal } from 'reducers/OptInModal';
import { tableViewReducer, TableView } from 'reducers/TableView';

import { ticketPreferencesReducer } from 'reducers/TicketPreferences';

import {
  requestsManagerReducer,
  RequestsManager,
} from 'reducers/RequestsManager';

import { selectedTicketReducer } from 'reducers/SelectedTicket';

import { ticketsReducer, Tickets } from 'reducers/Tickets';
import { Ticket, Preferences, TicketPreference, SurveyEligility } from 'types';
import {
  pollingTicketsReducer,
  PollingTicketStatus,
} from 'reducers/PollingTickets';
import { browserDeatilsReducer, BrowserDetails } from 'reducers/BrowserDetails';
import { Drawer, drawerReducer } from 'reducers/Drawer';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const middlewares = [thunkMiddleware, analyticsMiddleware];

const reducers = {
  accounts: accountsReducer,
  attachments: attachmentsReducer,
  attachmentsAWS: attachmentsAWSReducer,
  bulkClose: bulkCloseReducer,
  categories: categoriesReducer,
  Collections: collectionsManagerReducer,
  exportTickets: exportTicketsReducer,
  intercept: interceptReducer,
  resumeTicketModal: resumeTicketModalReducer,
  newComment: newCommentReducer,
  newTicket: newTicketReducer,
  nps: npsReducer,
  csat: customerSatisfactionSurveyReducer,
  pagination: paginationReducer,
  pollingTicket: pollingTicketsReducer,
  portalPreferences: portalPreferencesReducer,
  recipients: recipientsReducer,
  Requests: requestsManagerReducer,
  selectedTicket: selectedTicketReducer,
  tickets: ticketsReducer,
  optInModal: optInReducer,
  browserDetails: browserDeatilsReducer,
  tableView: tableViewReducer,
  ticketPreferences: ticketPreferencesReducer,
  drawer: drawerReducer,
};

const store = createStore<AppState>(
  combineReducers(reducers),
  composeEnhancers(applyMiddleware(...middlewares)),
);

/**
 * Allows use of Chrome and FF redux debugging extensions without adding
 * dependency to the app
 */
declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__: Function;
    PORTAL_DATA: {
      domainId: string;
      tenants: string[];
      userId: string;
      username: string;
      federatedIdp: string;
      contactId: string;
      email: string;
    };
    store: Store<AppState>;
    // eslint-disable-next-line
    DD_RUM: any;
  }
}

if (process.env.REACT_APP_MOCK_TIME === 'true') {
  window.store = store;
}

/* Exports */

export type AppState = {
  accounts: Accounts;
  attachments: Attachments;
  attachmentsAWS: AttachmentsAWS;
  bulkClose: BulkClose;
  categories: Categories;
  Collections: CollectionsManager;
  exportTickets: ExportTickets;
  intercept: InterceptModal;
  resumeTicketModal: ResumeTicket;
  newComment: NewComment;
  newTicket: NewTicket;
  nps: Rating;
  csat: RatingCSAT;
  pagination: Pagination;
  recipients: Recipients;
  pollingTicket: PollingTicketStatus;
  portalPreferences: Preferences;
  Requests: RequestsManager;
  selectedTicket: Ticket;
  tickets: Tickets;
  optInModal: OptInModal;
  browserDetails: BrowserDetails;
  tableView: TableView;
  ticketPreferences: TicketPreference;
  isEligible: SurveyEligility;
  drawer: Drawer;
};

export * from 'reducers/Accounts';
export * from 'reducers/Attachments';
export * from 'reducers/AttachmentsAWS';
export * from 'reducers/BulkClose';
export * from 'reducers/Category';
export * from 'reducers/CollectionsManager';
export * from 'reducers/ExportTickets';
export * from 'reducers/InterceptModal';
export * from 'reducers/NewComment';
export * from 'reducers/NewTicket';
export * from 'reducers/Nps';
export * from 'reducers/CustomerSatisfactionSurvey';
export * from 'reducers/OptInModal';
export * from 'reducers/Pagination';
export * from 'reducers/PollingTickets';
export * from 'reducers/PortalPreferences';
export * from 'reducers/ResumeTicket';
export * from 'reducers/Recipients';
export * from 'reducers/RequestsManager';
export * from 'reducers/ResumeTicket';
export * from 'reducers/SelectedTicket';
export * from 'reducers/Tickets';
export * from 'reducers/TicketPreferences';
export * from 'reducers/TableView';
export * from 'reducers/BrowserDetails';
export * from 'reducers/Drawer';
export { store };
